/**
 * Badge Status to Variant Map
 *
 * Maps status values to BadgeStatus component variant values
 * for consistent color display across the application.
 *
 * Each status maps to its own distinct variant for accurate color representation.
 */
export const BADGE_STATUS_MAP = {
  Draft: 'draft',
  Incomplete: 'error',
  'Not Submitted': 'notSubmitted',
  Submitted: 'submitted',
  Approved: 'approved',
  Paid: 'paid',
  Successful: 'successful',
  Error: 'error',
  'In Progress': 'inProgress',
  Pending: 'pending',
  Waitlist: 'waitlist',
  Alert: 'alert',
  Canceled: 'canceled',
  'Screening Complete': 'screeningComplete',
  Denied: 'denied',
  Open: 'open',
  Closed: 'closed',
  On: 'on',
  Off: 'off',
  'No Data': 'noData',
  'Resident Repayment Generating': 'residentRepaymentGenerating',
  'Resident Repayment Impacted': 'residentRepaymentImpacted',
  'Owner Repayment': 'ownerRepayment',
  Reopened: 'reopened',
  'Corrections Started': 'correctionsStarted',
};

export const BADGE_STATUS_MESSAGE_MAP = {
  Draft: 'submissionStatusDraft',
  'Not Submitted': 'submissionStatusNotSubmitted',
  Submitted: 'submissionStatusSubmitted',
  Approved: 'submissionStatusApproved',
  Paid: 'submissionStatusPaid',
  Successful: 'submissionStatusSuccessful',
  Error: 'submissionStatusError',
  'In Progress': 'statusInProgress',
  Pending: 'statusPending',
  Waitlist: 'statusWaitlist',
  Alert: 'statusAlert',
  Canceled: 'statusCanceled',
  'Screening Complete': 'statusScreeningComplete',
  Denied: 'statusDenied',
  Open: 'statusOpen',
  Closed: 'statusClosed',
  On: 'statusOn',
  Off: 'statusOff',
  'No Data': 'statusNoData',
  'Resident Repayment Generating': 'statusResidentRepaymentGenerating',
  'Resident Repayment Impacted': 'statusResidentRepaymentImpacted',
  'Owner Repayment': 'statusOwnerRepayment',
  Reopened: 'statusReopened',
  'Corrections Started': 'statusCorrectionsStarted',
};

// Normalize to title case: 'in progress' → 'In Progress'
const toTitleCase = (statusValue) =>
  statusValue
    ?.toLowerCase()
    .split(' ')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ') ?? '';

// Safe lookup with fallback
const getFromMap = (status, map, fallback) =>
  map[status] ?? map[toTitleCase(status)] ?? fallback;

export const getStatusBadgeVariant = (status) =>
  getFromMap(status, BADGE_STATUS_MAP, 'draft');

export const getHUDVoucherStatusBadgeVariant = (status) =>
  toTitleCase(status) === 'Draft'
    ? 'notSubmitted'
    : getFromMap(status, BADGE_STATUS_MAP, 'notSubmitted');

export const getHUDVoucherBadgeConfig = (status) => {
  if (toTitleCase(status) === 'Draft') {
    return {
      status: 'custom',
      variant: 'filled',
      sx: { color: 'warning.main', backgroundColor: 'warning.lighter' },
    };
  }

  return {
    status: getHUDVoucherStatusBadgeVariant(status),
    variant: 'filled',
  };
};
